import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, RefreshCcw, Save, XCircle, Edit, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';

const baseInputClasses =
  'w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 focus:border-lime-500 focus:ring-2 focus:ring-lime-200 focus:ring-opacity-50 transition-all duration-200 hover:border-stone-400';
const ghostButtonClasses =
  'inline-flex items-center justify-center rounded-lg border border-stone-200 px-3 py-2 text-sm font-medium text-stone-700 bg-white shadow-sm hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50';
const primaryButtonClasses =
  'inline-flex items-center justify-center rounded-lg bg-lime-500 px-4 py-2 text-sm font-semibold text-stone-900 shadow hover:bg-lime-400 disabled:cursor-not-allowed disabled:opacity-50';

const renderColumnValue = (column, record) =>
  typeof column.render === 'function' ? column.render(record[column.accessor], record) : record[column.accessor];

const Field = ({ field, value, onChange, disabled, formState, setFormState }) => {
  // Handle custom components
  if (field.type === 'custom' && field.component) {
    const CustomComponent = field.component;
    return (
      <CustomComponent
        value={value}
        onChange={(newValue) => onChange({ target: { name: field.name, value: newValue } })}
        {...(field.props || {})}
      />
    );
  }

  // Handle custom onChange handlers
  const handleOnChange = (event) => {
    if (field.onChange) {
      field.onChange(event, formState, setFormState);
    } else {
      onChange(event);
    }
  };

  const common = {
    id: field.name,
    name: field.name,
    value: value ?? '',
    onChange: handleOnChange,
    className: baseInputClasses,
    placeholder: field.placeholder,
    disabled,
  };

  if (field.type === 'textarea') {
    return <textarea {...common} rows={field.rows || 4} />;
  }

  if (field.type === 'select') {
    return (
      <select {...common}>
        <option value="">Select {field.label}</option>
        {field.options?.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <label className="inline-flex items-center gap-3 text-sm text-stone-800 cursor-pointer">
        <div className="relative">
          <input
            type="checkbox"
            name={field.name}
            checked={Boolean(value)}
            onChange={(e) => onChange({ target: { name: field.name, value: e.target.checked } })}
            className="sr-only"
            disabled={disabled}
          />
          <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 ${
            value 
              ? 'bg-lime-500 border-lime-500' 
              : 'bg-white border-stone-300 hover:border-stone-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            {value && (
              <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
        <span className={disabled ? 'opacity-50' : ''}>{field.label}</span>
      </label>
    );
  }

  return <input {...common} type={field.type || 'text'} />;
};

export const ResourceManager = ({
  table,
  title,
  description,
  columns,
  formFields,
  defaultValues = {},
  select = '*',
  order,
  transformBeforeSave,
  onEdit,
  onReady,
  onSaveSuccess,
  extraButtons = [],
  externalRecords = null,
  searchable = true,
  searchPlaceholder = 'Search...',
  searchFields = ['title', 'name', 'location'],
  itemsPerPage = 10,
  paginated = true,
}) => {
  const { records, loading, error, refresh, upsertRecord, deleteRecord } = useSupabaseResource(
    table,
    { select, order }
  );
  const [formState, setFormState] = useState(defaultValues);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const defaultSignature = useMemo(() => JSON.stringify(defaultValues), [defaultValues]);

  useEffect(() => {
    // When defaults change (e.g., board roles fetched), update form if not editing
    if (!editingId) {
      setFormState(defaultValues);
    }
  }, [defaultSignature, defaultValues, editingId]);

  const resetForm = () => {
    setFormState(defaultValues);
    setEditingId(null);
    setFormError(null);
    setSuccessMessage('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const payload = transformBeforeSave
        ? transformBeforeSave({ ...formState, id: editingId })
        : { ...formState, id: editingId };
      await upsertRecord(payload);
      if (!externalRecords) {
        await refresh(); // keep internal data in sync
      }
      if (typeof onSaveSuccess === 'function') {
        await onSaveSuccess(payload);
      }
      setSuccessMessage(`Saved ${title} successfully.`);
      resetForm();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    const processedRecord = onEdit ? onEdit(record) : record;
    setFormState({ ...defaultValues, ...processedRecord });
    setFormError(null);
    setSuccessMessage('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await deleteRecord(id);
    } catch (err) {
      setFormError(err.message);
    }
  };

  // Filter and paginate records
  const filteredAndPaginatedRecords = useMemo(() => {
    const allRecords = Array.isArray(externalRecords) ? externalRecords : records;
    
    // Apply search filter
    let filteredRecords = allRecords;
    if (searchable && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredRecords = allRecords.filter(record => {
        return searchFields.some(field => {
          const value = record[field];
          if (typeof value === 'string') {
            return value.toLowerCase().includes(query);
          }
          return false;
        });
      });
    }

    // Apply pagination
    if (paginated) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return {
        records: filteredRecords.slice(startIndex, endIndex),
        totalItems: filteredRecords.length,
        totalPages: Math.ceil(filteredRecords.length / itemsPerPage),
        currentPage,
      };
    }

    return {
      records: filteredRecords,
      totalItems: filteredRecords.length,
      totalPages: 1,
      currentPage: 1,
    };
  }, [externalRecords, records, searchQuery, searchFields, searchable, currentPage, itemsPerPage, paginated]);

  const { records: displayRecords, totalItems, totalPages } = filteredAndPaginatedRecords;
  const priceColumn = useMemo(() => columns.find((col) => col.accessor === 'price'), [columns]);
  const detailColumns = useMemo(
    () => columns.filter((col) => !['title', 'price'].includes(col.accessor)),
    [columns]
  );

  const tableBody = useMemo(() => {
    if (loading) {
      return (
        <tr>
          <td colSpan={columns.length + 1} className="py-6 text-center text-stone-500">
            Loading {title}...
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <td colSpan={columns.length + 1} className="py-6 text-center text-red-500">
            {error}
          </td>
        </tr>
      );
    }

    if (totalItems === 0) {
      if (searchable && searchQuery.trim()) {
        return (
          <tr>
            <td colSpan={columns.length + 1} className="py-6 text-center text-stone-500">
              No records found for "{searchQuery}".
            </td>
          </tr>
        );
      }
      return (
        <tr>
          <td colSpan={columns.length + 1} className="py-6 text-center text-stone-500">
            No records yet.
          </td>
        </tr>
      );
    }
    return displayRecords.map((record) => (
      <tr key={record.id} className="border-b border-stone-100 text-sm hover:bg-stone-50/50 transition-all duration-150">
        {columns.map((col) => (
          <td key={col.accessor} className="px-6 py-4 align-top text-stone-800">
            {renderColumnValue(col, record)}
          </td>
        ))}
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => handleEdit(record)}
              className="inline-flex items-center rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-400 hover:shadow-md transition-all duration-150"
            >
              <Edit className="mr-1.5 h-3.5 w-3.5" /> Edit
            </button>
            <button
              onClick={() => handleDelete(record.id)}
              className="inline-flex items-center rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-300 hover:shadow-md transition-all duration-150"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </td>
      </tr>
    ));
  }, [columns, deleteRecord, error, loading, filteredAndPaginatedRecords, searchable, searchQuery, title]);

  useEffect(() => {
    if (typeof onReady === 'function') {
      onReady({ refresh, upsertRecord, deleteRecord });
    }
  }, [deleteRecord, onReady, refresh, upsertRecord]);

  return (
    <section className="rounded-2xl border border-stone-200 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
      <header className="flex flex-col gap-4 border-b border-stone-100 bg-gradient-to-r from-stone-50/80 to-stone-50/40 px-6 py-5 sm:px-8 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-stone-900">{title}</h2>
          <p className="text-sm text-stone-500">{description}</p>
        </div>
        <div className="flex w-full flex-col gap-3 md:max-w-2xl md:flex-row md:flex-wrap md:items-center md:justify-end">
          {searchable && (
            <div className="relative w-full md:w-auto md:min-w-[260px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="w-full rounded-lg border border-stone-300 bg-white py-3 pl-11 pr-4 text-sm shadow-sm transition-all duration-200 hover:border-stone-400 focus:border-lime-500 focus:outline-none focus:ring-2 focus:ring-lime-200 focus:ring-opacity-50"
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2 md:justify-end">
            <button onClick={refresh} className={`${ghostButtonClasses} w-full md:w-auto`}>
              <RefreshCcw className="mr-1.5 h-4 w-4" />
              Refresh
            </button>
            {editingId && (
              <button onClick={resetForm} className={`${ghostButtonClasses} w-full md:w-auto`}>
                <XCircle className="mr-1.5 h-4 w-4" />
                Cancel Edit
              </button>
            )}
            {extraButtons.map((btn, index) => (
              <button
                key={index}
                onClick={btn.onClick}
                disabled={btn.disabled}
                className={
                  btn.className ||
                  `${ghostButtonClasses} w-full md:w-auto ${
                    btn.primary ? 'bg-lime-500 text-stone-900 hover:bg-lime-400 border-lime-500 shadow' : ''
                  }`
                }
              >
                {btn.icon && <btn.icon className="mr-1.5 h-4 w-4" />}
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="grid gap-6 px-4 py-6 lg:grid-cols-[2fr,1fr] lg:gap-8 lg:px-6">
        <div className="space-y-4">
          <div className="hidden overflow-hidden rounded-xl border border-stone-100 bg-white shadow-md md:block">
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-200 text-left">
                <thead className="bg-gradient-to-r from-stone-50 to-stone-100 text-xs font-semibold uppercase tracking-wide text-stone-600">
                  <tr>
                    {columns.map((col) => (
                      <th key={col.accessor} className="px-6 py-4 text-left">
                        {col.label}
                      </th>
                    ))}
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 bg-white text-sm">{tableBody}</tbody>
              </table>
            </div>
          </div>

          <div className="space-y-3 md:hidden">
            {loading && (
              <div className="rounded-xl border border-stone-100 bg-white px-4 py-6 text-sm text-stone-500">
                Loading {title}...
              </div>
            )}
            {error && (
              <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
                {error}
              </div>
            )}
            {!loading && !error && totalItems === 0 && (
              <div className="rounded-xl border border-stone-100 bg-white px-4 py-6 text-sm text-stone-600">
                {searchable && searchQuery.trim() ? `No records found for "${searchQuery}".` : 'No records yet.'}
              </div>
            )}
            {!loading &&
              !error &&
              totalItems > 0 &&
              displayRecords.map((record) => (
                <div
                  key={record.id}
                  className="space-y-3 rounded-xl border border-stone-100 bg-white px-5 py-5 shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      {record.status && (
                        <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-1 text-[11px] font-semibold uppercase text-stone-700">
                          {record.status}
                        </span>
                      )}
                      <h3 className="text-base font-semibold text-stone-900">{record.title || 'Untitled'}</h3>
                      {record.location && <p className="text-sm text-stone-600">{record.location}</p>}
                    </div>
                    {priceColumn && (
                      <span className="rounded-full bg-stone-900 px-3 py-1 text-xs font-semibold text-lime-100 shadow-sm">
                        {renderColumnValue(priceColumn, record)}
                      </span>
                    )}
                  </div>
                  <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {detailColumns.map((col) => (
                      <div key={col.accessor} className="rounded-lg bg-stone-50 px-3 py-2">
                        <dt className="text-[11px] font-semibold uppercase text-stone-500">{col.label}</dt>
                        <dd className="mt-1 text-sm text-stone-800">{renderColumnValue(col, record) ?? 'N/A'}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleEdit(record)}
                      className="inline-flex flex-1 items-center justify-center rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 sm:flex-none sm:px-4"
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(record.id)}
                      className="inline-flex flex-1 items-center justify-center rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 sm:flex-none sm:px-4"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>

          {paginated && totalPages > 1 && (
            <div className="flex flex-col gap-4 rounded-xl border border-stone-100 bg-white px-6 py-4 text-sm shadow-md sm:flex-row sm:items-center sm:justify-between">
              <div className="text-stone-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`${ghostButtonClasses} h-10 px-3 py-2`}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </button>
                <div className="flex flex-wrap items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold ${
                          currentPage === pageNum
                            ? 'bg-lime-500 text-stone-900 shadow'
                            : 'text-stone-700 bg-white border border-stone-300 hover:bg-stone-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`${ghostButtonClasses} h-10 px-3 py-2`}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <form
          className="rounded-xl border border-stone-100 bg-gradient-to-br from-stone-50/80 to-stone-50/40 p-6 sm:p-7 lg:sticky lg:top-6 shadow-md"
          onSubmit={handleSubmit}
        >
          <h3 className="mb-4 text-base font-semibold text-stone-900">
            {editingId ? `Edit ${title}` : `Add ${title}`}
          </h3>
          <div className="space-y-4">
            {(typeof formFields === 'function' ? formFields(formState) : formFields).map((field) => (
              <div key={field.name} className="space-y-1">
                {field.type !== 'checkbox' && (
                  <label htmlFor={field.name} className="text-xs font-medium uppercase tracking-wide text-stone-500">
                    {field.label}
                  </label>
                )}
                <Field
                  field={field}
                  value={formState[field.name]}
                  disabled={field.disabled}
                  formState={formState}
                  setFormState={setFormState}
                  onChange={(event) => {
                    const { name, value } = event.target;
                    setFormState((prev) => ({ ...prev, [name]: value }));
                  }}
                />
              </div>
            ))}
          </div>
          {formError && <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}
          {successMessage && (
            <p className="mt-4 rounded bg-lime-50 px-3 py-2 text-sm text-lime-700">{successMessage}</p>
          )}
          <button
            type="submit"
            disabled={saving}
            className="mt-6 inline-flex w-full items-center justify-center rounded-lg bg-lime-500 px-6 py-3 text-sm font-semibold text-stone-900 shadow-lg hover:bg-lime-400 hover:shadow-xl transition-all duration-200 disabled:opacity-50"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : editingId ? 'Update Property' : 'Create Property'}
          </button>
        </form>
      </div>
    </section>
  );
};
